/**
 * 检测当前设备是否为iPad
 * 考虑了iPadOS 13+的特殊情况
 * @returns {boolean} - 如果是iPad则返回true，否则返回false
 */
export function isiPad() {
  // 1. 检测iPadOS 13+（最可靠的方法）
  // iPadOS 13+ 会伪装成Mac，但保留触摸支持
  const isIPadOS13Plus = navigator.platform === 'MacIntel' &&
    (navigator.maxTouchPoints > 1 ||
      'ontouchstart' in window);

  // 2. 检测传统iPad（旧版iOS）
  const isLegacyIPad = /ipad/.test(navigator.userAgent.toLowerCase());

  // 3. 检测iPad模拟器（开发工具模拟）
  const isSimulatedIPad = /macintosh/.test(navigator.userAgent.toLowerCase()) &&
    navigator.maxTouchPoints > 1 &&
    window.innerWidth <= 1024;

  // 4. 综合判断
  return isIPadOS13Plus || isLegacyIPad || isSimulatedIPad;
}

/**
 * 扩展更全面的设备检测工具
 */
export const DeviceDetector = {
  /**
   * 检测是否为iPad
   */
  isiPad() {
    const isIPadOS13Plus = navigator.platform === 'MacIntel' &&
      (navigator.maxTouchPoints > 1 ||
        'ontouchstart' in window);

    const isLegacyIPad = /ipad/.test(navigator.userAgent.toLowerCase());

    return isIPadOS13Plus || isLegacyIPad;
  },

  /**
   * 检测是否为iPhone
   */
  isiPhone() {
    return /iphone/.test(navigator.userAgent.toLowerCase()) &&
      !this.isiPad();
  },

  /**
   * 检测是否为iOS设备
   */
  isiOS() {
    return this.isiPad() || this.isiPhone() ||
      /ipod/.test(navigator.userAgent.toLowerCase());
  },

  /**
   * 检测是否为Android设备
   */
  isAndroid() {
    return /android/.test(navigator.userAgent.toLowerCase()) &&
      !/windows phone/.test(navigator.userAgent.toLowerCase());
  },

  /**
   * 检测是否为移动设备（手机或平板）
   */
  isMobile() {
    return this.isiOS() || this.isAndroid() ||
      /blackberry|windows phone|opera mini|iemobile/.test(navigator.userAgent.toLowerCase());
  },

  /**
   * 检测是否为平板设备（包括iPad和Android平板）
   */
  isTablet() {
    if (this.isiPad()) return true;

    // 检测Android平板
    return /android/.test(navigator.userAgent.toLowerCase()) &&
      !/mobile/.test(navigator.userAgent.toLowerCase()) &&
      window.innerWidth > 768;
  },

  /**
   * 获取设备详细信息
   */
  getDeviceInfo() {
    return {
      isiPad: this.isiPad(),
      isiPhone: this.isiPhone(),
      isiOS: this.isiOS(),
      isAndroid: this.isAndroid(),
      isMobile: this.isMobile(),
      isTablet: this.isTablet(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0
    };
  }
};

/**
 * 平板设备检测工具
 * 特别针对谷歌浏览器模拟平板板场景优化
 */
export const TabletDetector = {
  /**
   * 检测当前设备是否为平板（包括真实设备和浏览器模拟）
   * @returns {boolean} - 如果是平板则返回true，否则返回false
   */
  isTablet() {
    // 1. 检测真实iPad（包括iPadOS 13+）
    const isIPad = this._detectIPad();

    // 2. 检测Android平板
    const isAndroidTablet = this._detectAndroidTablet();

    // 3. 检测浏览器模拟的平板
    const isSimulatedTablet = this._detectSimulatedTablet();

    return isIPad || isAndroidTablet || isSimulatedTablet;
  },

  /**
   * 检测是否为iPad
   */
  _detectIPad() {
    // iPadOS 13+ 检测
    const isIPadOS13Plus = navigator.platform === 'MacIntel' &&
      (navigator.maxTouchPoints > 1 || 'ontouchstart' in window) &&
      window.innerWidth <= 1024;

    // 传统iPad检测
    const isLegacyIPad = /ipad/.test(navigator.userAgent.toLowerCase());

    return isIPadOS13Plus || isLegacyIPad;
  },

  /**
   * 检测是否为Android平板
   */
  _detectAndroidTablet() {
    const userAgent = navigator.userAgent.toLowerCase();
    return /android/.test(userAgent) &&
      !/mobile/.test(userAgent) &&
      window.innerWidth > 768;
  },

  /**
   * 检测是否为浏览器模拟的平板
   */
  _detectSimulatedTablet() {
    const userAgent = navigator.userAgent.toLowerCase();

    // 检测Chrome开发工具模拟的平板
    const isChromeSimulatedTablet = /chrome/.test(userAgent) &&
      (window.innerWidth > 768 && window.innerWidth <= 1024) &&
      (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);

    // 检测其他浏览器模拟的平板
    const isOtherSimulatedTablet = (window.innerWidth > 768 && window.innerWidth <= 1024) &&
      (navigator.maxTouchPoints > 0 || 'ontouchstart' in window) &&
      !/iphone|ipod|android.*mobile|blackberry|windows phone/.test(userAgent);

    return isChromeSimulatedTablet || isOtherSimulatedTablet;
  },

  /**
   * 获取平板类型
   */
  getTabletType() {
    if (this._detectIPad()) return 'ipad';
    if (this._detectAndroidTablet()) return 'android_tablet';
    if (this._detectSimulatedTablet()) return 'simulated_tablet';
    return 'not_tablet';
  },

  /**
   * 获取设备详细信息
   */
  getDeviceInfo() {
    return {
      isTablet: this.isTablet(),
      tabletType: this.getTabletType(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      isSimulated: this._detectSimulatedTablet()
    };
  },

  /**
   * 监听浏览器模拟平板的特殊处理
   */
  handleChromeSimulation() {
    // 对于Chrome模拟的平板，添加特殊的CSS类
    if (this._detectSimulatedTablet()) {
      document.body.classList.add('chrome-simulated-tablet');

      // 模拟触摸事件支持
      if (!('ontouchstart' in window)) {
        console.documentElement.addEventListener('touchstart', () => {}, true);
      }
    }
  }
};
